import React, { useEffect, useMemo, useState } from 'react';
import LinkDoCliente from '../shared/LinkDoCliente';
import { listarCongelados } from '../../../data/estoque';
import { listarSemenDoRelatorio } from '../../../data/semen';
import { clientesComEstoque } from '../../../utils/calculations/extratoDeEstoque';
import {
  Plus, User, Edit2, Trash2, Search, MapPin, MessageCircle, ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  filtrarClientes, FILTROS_DE_CLIENTE, identidadeDoCliente,
  locaisDoCliente, localDeCobranca, nomeDoLocal,
} from '../../../data/clients';
import { MARK } from '../../../constants';
import { conversaDoWhatsapp } from '../../../data/cliente';

/**
 * A carteira, em LISTA.
 *
 * Eram cartões de 6rem de altura, três por linha: catorze clientes ocupavam duas
 * telas e meia, e achar um pelo nome exigia varrer a grade em zigue-zague. A
 * pergunta que se faz aqui é de comparação — quem tem serviço aberto, quem é
 * daquela cidade, qual é o telefone — e comparação se faz em coluna, onde o olho
 * desce reto.
 *
 * O LINK DE ACOMPANHAMENTO MORA NA LINHA ABERTA, e não na fechada. Ele é um
 * painel com estado e uma consulta por cliente: no cartão eram catorze consultas
 * ao abrir a aba, para um link que se manda a um cliente por vez. Agora só o
 * cliente aberto consulta o dele.
 *
 * DO ESTOQUE, AQUI SÓ O NÚMERO. Por um tempo a linha aberta trazia o painel
 * inteiro — saldo por cruzamento, extrato, PDF —, e era detalhe demais para uma
 * tela de cadastro: quem abre a carteira quer saber quem é o cliente, e o número
 * ao lado de "serviços ativos" já responde se há algo dele no botijão. O detalhe
 * mora na aba de Estoque, na visão por cliente, que é a tela de quem está com a
 * mão no nitrogênio.
 *
 * As duas consultas do estoque são do LABORATÓRIO INTEIRO, uma vez, e não uma por
 * cliente: o número entra em todas as linhas de uma vez, e catorze consultas para
 * preencher catorze células seria pagar caro por uma coluna.
 */
const ClientsTab = ({
  clients,
  services,
  canEdit,
  openModal,
  handleDelete,
  getServiceStatus,
  // Para o estoque do cliente: de qual laboratório ler, e o nome que vai no
  // cabeçalho do PDF.
  currentLab,
}) => {
  const [busca, setBusca] = useState('');
  const [atividade, setAtividade] = useState('todos');
  // Um por vez: são painéis com consulta própria, e abrir catorze de uma vez
  // devolveria a aba ao problema que a lista resolveu.
  const [aberto, setAberto] = useState(null);
  const [estoque, setEstoque] = useState([]);

  /**
   * O estoque do laboratório, uma vez, para a coluna.
   *
   * Falha em silêncio de propósito: a carteira é de cadastro, e não abrir por
   * causa do botijão seria trocar o principal pelo acessório. Sem isso a coluna
   * fica vazia e o resto da tela continua servindo.
   */
  useEffect(() => {
    if (!currentLab?.id) return undefined;
    let cancelado = false;
    Promise.all([
      listarCongelados(currentLab.id),
      listarSemenDoRelatorio({ laboratoryId: currentLab.id }),
    ])
      .then(([congelados, partidas]) => {
        if (cancelado) return;
        setEstoque(clientesComEstoque(congelados, partidas, clients));
      })
      .catch(e => console.error('Estoque da carteira:', e));
    return () => { cancelado = true; };
  }, [currentLab?.id, clients]);

  const guardadoPorCliente = useMemo(
    () => Object.fromEntries(estoque.filter(x => x.client_id).map(x => [x.client_id, x])),
    [estoque]);

  /**
   * Quantos serviços abertos cada cliente tem.
   *
   * Contado UMA vez para a lista inteira. O cartão fazia
   * `services.filter(...)` dentro do `map`, o que é uma varredura da lista de
   * serviços por cliente — com 14 clientes não incomoda, mas é a conta que o
   * filtro por atividade precisaria refazer a cada tecla digitada.
   */
  const ativosPorCliente = useMemo(() => {
    const mapa = {};
    (services || []).forEach(s => {
      if (getServiceStatus(s) === 'Finalizado') return;
      mapa[s.client_id] = (mapa[s.client_id] || 0) + 1;
    });
    return mapa;
  }, [services, getServiceStatus]);

  const visiveis = useMemo(
    () => filtrarClientes(clients, { busca, atividade }, ativosPorCliente),
    [clients, busca, atividade, ativosPorCliente]);

  const temCadastro = (clients || []).length > 0;
  // Duas colunas a mais desde que o estoque entrou: embriões e doses.
  const colunas = canEdit() ? 8 : 7;

  /** Meia dose existe, e "3,5" é como se fala. */
  const doses = (v) => {
    const n = Number(v) || 0;
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-900">Clientes</h2>
        {canEdit() && (
          <button
            onClick={() => openModal('client')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Plus size={16} />
            <span>Novo Cliente</span>
          </button>
        )}
      </div>

      {/* A barra só aparece havendo cadastro: sobre lista vazia ela é um
          controle que não controla nada, e empurra a mensagem de "comece por
          aqui" para baixo da dobra. */}
      {temCadastro && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por cliente, contato, fazenda, cidade ou telefone"
              className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex gap-1.5 shrink-0">
            {FILTROS_DE_CLIENTE.map(f => (
              <button
                key={f.id}
                onClick={() => setAtividade(f.id)}
                className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap border transition-colors ${
                  atividade === f.id
                    ? 'bg-blue-600 border-blue-600 text-white font-medium'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                {f.rotulo}
              </button>
            ))}
          </div>
          {(busca || atividade !== 'todos') && (
            <span className="text-xs text-slate-500 self-center whitespace-nowrap">
              {visiveis.length} de {clients.length} cliente(s)
            </span>
          )}
        </div>
      )}

      {visiveis.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-3 py-2 font-medium text-slate-700">Cliente</th>
                <th className="text-left px-3 py-2 font-medium text-slate-700 whitespace-nowrap">Telefone</th>
                <th className="text-left px-3 py-2 font-medium text-slate-700">Cidade</th>
                {/* À direita porque é número: alinhado, o olho compara a coluna
                    inteira de uma vez. */}
                <th className="text-right px-3 py-2 font-medium text-slate-700 whitespace-nowrap">Ativos</th>
                {/* Embrião e dose em colunas separadas: são unidades diferentes,
                    e somá-las num número só diria uma quantidade que não existe. */}
                <th className="text-right px-3 py-2 font-medium text-slate-700 whitespace-nowrap">Embriões</th>
                <th className="text-right px-3 py-2 font-medium text-slate-700 whitespace-nowrap">Doses</th>
                <th className="px-3 py-2" />
                {canEdit() && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visiveis.map(client => {
                // A mesma regra do dashboard: quem atende o telefone em cima, a
                // fazenda embaixo. O cartão mostrava o contrário dos outros.
                const { principal, secundario } = identidadeDoCliente(client);
                const ativos = ativosPorCliente[client.id] || 0;
                const guardado = guardadoPorCliente[client.id] || { embrioes: 0, doses: 0 };
                const expandido = aberto === client.id;
                // O telefone da carteira é para usar, não para ler: quem abre
                // esta aba com uma pergunta de cliente na cabeça vai falar com
                // ele. Número que o cadastro não deixa confiar fica texto.
                const conversa = conversaDoWhatsapp(client.phone);
                const locais = locaisDoCliente(client);
                const cidades = [...new Set(locais
                  .map(l => [l.cidade, l.uf].filter(Boolean).join(' — '))
                  .filter(Boolean))];
                // O endereço do boleto aparece só quando há um: é o que se
                // procura ao emitir, e "—" numa linha a mais não ajuda ninguém.
                const cobranca = localDeCobranca(client);
                const enderecoDeCobranca = cobranca && [cobranca.endereco, cobranca.cidade, cobranca.uf, cobranca.cep]
                  .filter(Boolean).join(', ');
                const comMapa = locais.filter(l => l.link);

                return (
                  <React.Fragment key={client.id}>
                    <tr className={expandido ? 'bg-slate-50' : 'hover:bg-slate-50 transition-colors'}>
                      <td className="px-3 py-2">
                        <p className="font-medium text-slate-900">{principal}</p>
                        {secundario && (
                          <p className="text-xs text-slate-500">{secundario}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                        {conversa ? (
                          <a
                            href={conversa}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir conversa no WhatsApp"
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                          >
                            <MessageCircle size={13} /> {client.phone}
                          </a>
                        ) : (client.phone || '—')}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {cidades.join(' · ') || '—'}
                        {enderecoDeCobranca && (
                          <p className="text-xs text-slate-500" title="Endereço de cobrança">
                            {MARK.COBRANCA} {enderecoDeCobranca}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {ativos > 0
                          ? <span className="font-semibold text-slate-900">{ativos}</span>
                          : <span className="text-slate-300">0</span>}
                      </td>
                      {/* O NÚMERO, E SÓ. O detalhe do que está guardado — saldo
                          por cruzamento, extrato, PDF — mora na aba de Estoque,
                          na visão por cliente. Aqui a pergunta é quem é o
                          cliente, e o número já responde se há algo dele lá. */}
                      <td className="px-3 py-2 text-right tabular-nums">
                        {guardado.embrioes > 0
                          ? <span className="font-semibold text-slate-900">{guardado.embrioes}</span>
                          : <span className="text-slate-300">0</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {guardado.doses > 0
                          ? <span className="font-semibold text-slate-900">{doses(guardado.doses)}</span>
                          : <span className="text-slate-300">0</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {/* A localização continua a um clique de quem vai sair
                            para a fazenda: dentro do modal de edição ela
                            estaria a dois, no meio da estrada. */}
                        {/* Um mapa por local; com mais de um, o nome diz qual. */}
                        {comMapa.map(l => (
                          <a
                            key={l.id}
                            href={l.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`Abrir localização — ${nomeDoLocal(l)}`}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                          >
                            <MapPin size={13} /> {comMapa.length > 1 ? nomeDoLocal(l) : 'Mapa'}
                          </a>
                        ))}
                      </td>
                      {canEdit() && (
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setAberto(a => (a === client.id ? null : client.id))}
                              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                              title="Link de acompanhamento do cliente"
                            >
                              {expandido ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              Link
                            </button>
                            <button
                              onClick={() => openModal('client', client)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Editar"
                            >
                              <Edit2 size={15} />
                            </button>
                            <button
                              onClick={() => handleDelete('client', client.id)}
                              className="text-slate-400 hover:text-red-600 transition-colors"
                              title="Excluir"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>

                    {expandido && canEdit() && (
                      <tr className="bg-slate-50">
                        <td colSpan={colunas} className="px-3 pb-3">
                          <LinkDoCliente client={client} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="text-center py-12 text-slate-500">
            <User className="mx-auto mb-4 text-slate-400" size={48} />
            {/* Nada encontrado e nada cadastrado são situações diferentes, e
                oferecer "Adicionar Cliente" a quem só digitou errado manda a
                pessoa cadastrar de novo quem ela já tem. */}
            {temCadastro ? (
              <>
                <p className="text-lg">Nenhum cliente com esse recorte</p>
                <p className="text-sm mt-2">
                  {busca ? <>Nada encontrado para <strong>{busca}</strong>.</> : 'Experimente outro filtro.'}
                </p>
                <button
                  onClick={() => { setBusca(''); setAtividade('todos'); }}
                  className="mt-4 text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Limpar filtros
                </button>
              </>
            ) : (
              <>
                <p className="text-lg">Nenhum cliente cadastrado</p>
                <p className="text-sm mt-2">Comece adicionando seus primeiros clientes</p>
                {canEdit() && (
                  <button
                    onClick={() => openModal('client')}
                    className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Adicionar Cliente
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsTab;
